import React, { useMemo, useEffect } from 'react';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import FiltersCard from '../../../components/FiltersCard';
import { FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import { AllTasksFilterOptions, AllTasksFilters } from '../../../services/allTasksService';
import { formatStatusForDisplay, sortTaskStatuses } from '../../../utils/statusUtils';

/**
 * Filter state can hold either a list (the multiselects) or a bare string (deep links that predate
 * the multiselect conversion). Everything downstream works on the list form.
 */
const toValueList = (value: string | string[] | boolean | undefined): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (value === undefined || value === null || value === '' || typeof value === 'boolean') return [];
  return String(value).split(',').filter(Boolean);
};

interface AllTasksFiltersProps {
  filterOptions: AllTasksFilterOptions;
  filters: AllTasksFilters;
  onFilterChange: (name: string, value: string | string[] | boolean) => void;
  onClearFilters?: () => void;
  showApplyButton?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
  hasActiveFilters?: boolean;
  /** Filters that differ from their defaults, for the collapsed tablet bar's summary chip (W-iP6). */
  activeFilterCount?: number;
  /** Every applied filter hiding records, defaults included (V2-076, FLT-05). */
  appliedFilterCount?: number;
  /** The page's search, shown in the panel header bar (V2-077, SRCH-01). */
  search?: SearchFieldProps;
}

const AllTasksFiltersComponent: React.FC<AllTasksFiltersProps> = ({
  filterOptions,
  filters,
  onFilterChange,
  onClearFilters,
  showApplyButton = false,
  onApply,
  applyDisabled = false,
  hasActiveFilters = false,
  activeFilterCount,
  appliedFilterCount,
  search,
}) => {
  const selectedServiceAreas = useMemo(() => toValueList(filters.serviceArea), [filters.serviceArea]);
  const selectedForemanAreas = useMemo(() => toValueList(filters.foremanArea), [filters.foremanArea]);
  const selectedContractYears = useMemo(() => toValueList(filters.contractYear), [filters.contractYear]);
  const selectedActivities = useMemo(() => toValueList(filters.activityId), [filters.activityId]);
  const selectedProjects = useMemo(() => toValueList(filters.projectId), [filters.projectId]);
  const selectedTaskTypes = useMemo(() => toValueList(filters.taskType), [filters.taskType]);
  const selectedStatuses = useMemo(() => toValueList(filters.status), [filters.status]);

  /**
   * Service Area is a multiselect, so the cascades below match against a SET of selections rather
   * than one value: an empty set means "no service-area constraint" (show everything), and a
   * non-empty set is a union — a foreman area or project is kept when it belongs to ANY selected
   * service area. The set holds service-area ids (what the dropdown emits), but foreman areas are
   * still matched on name too because the param has historically accepted either.
   */
  const selectedServiceAreaSet = useMemo(() => new Set(selectedServiceAreas), [selectedServiceAreas]);

  const filteredForemanAreas = useMemo(() => {
    if (selectedServiceAreaSet.size === 0) {
      return filterOptions.foremanAreas;
    }
    return filterOptions.foremanAreas.filter((area) => {
      const serviceAreaName = area.serviceAreaName ?? '';
      const serviceAreaId = area.serviceAreaId?.toString() ?? '';
      return (
        (serviceAreaId !== '' && selectedServiceAreaSet.has(serviceAreaId)) ||
        (serviceAreaName !== '' && selectedServiceAreaSet.has(serviceAreaName))
      );
    });
  }, [filterOptions.foremanAreas, selectedServiceAreaSet]);

  const filteredProjects = useMemo(() => {
    if (selectedServiceAreaSet.size === 0) {
      return filterOptions.projects;
    }
    return filterOptions.projects.filter((project) => {
      const serviceAreaId = project.serviceAreaId?.toString() ?? '';
      return serviceAreaId !== '' && selectedServiceAreaSet.has(serviceAreaId);
    });
  }, [filterOptions.projects, selectedServiceAreaSet]);

  // Narrowing the service area can strand selections that are no longer offered, which would apply
  // an invisible filter. Prune them - but only once the options have actually loaded, otherwise the
  // first render (options still empty) would wipe selections restored from the URL.
  useEffect(() => {
    if (selectedForemanAreas.length === 0) return;
    if (filterOptions.foremanAreas.length === 0) return;
    const valid = new Set(filteredForemanAreas.map((a) => a.name));
    const updated = selectedForemanAreas.filter((name) => valid.has(name));
    if (updated.length !== selectedForemanAreas.length) {
      onFilterChange('foremanArea', updated);
    }
  }, [filterOptions.foremanAreas, filteredForemanAreas, onFilterChange, selectedForemanAreas]);

  useEffect(() => {
    if (selectedProjects.length === 0) return;
    if (filterOptions.projects.length === 0) return;
    const valid = new Set(filteredProjects.map((p) => p.id.toString()));
    const updated = selectedProjects.filter((id) => valid.has(id));
    if (updated.length !== selectedProjects.length) {
      onFilterChange('projectId', updated);
    }
  }, [filterOptions.projects, filteredProjects, onFilterChange, selectedProjects]);

  // The API returns statuses in whatever order the groupBy produced; present them in lifecycle order.
  const orderedStatuses = useMemo(
    () => sortTaskStatuses(filterOptions.statuses, (s) => s),
    [filterOptions.statuses]
  );

  return (
    <FiltersCard
      title="All Task Filters"
      subtitle="Filter tasks by service area, foreman area, contract year, activity, project, type, and status"
      collapsible={true}
      defaultCollapsed={false}
      showClearButton={true}
      onClear={onClearFilters}
      hasActiveFilters={hasActiveFilters}
      activeFilterCount={activeFilterCount}
      appliedFilterCount={appliedFilterCount}
      search={search}
      showApplyButton={showApplyButton}
      onApply={onApply}
      applyDisabled={applyDisabled}
    >
      {/* FilterGrid instead of Row/Col: tracks auto-fit at a readable minimum width and every
          control shares one look and one label baseline (W-M1). All filters here are multi-select;
          the /all-tasks endpoint parses each as a list (all-tasks.controller.ts asMultiValue). */}
      <FilterGrid>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={filterOptions.serviceAreas}
            value={selectedServiceAreas}
            onChange={(value) => onFilterChange('serviceArea', value.map((v) => String(v)))}
            getValue={(area) => area.id.toString()}
            formatOption={(area) => area.name}
            formatDisplay={(area) => area.name}
            placeholder="All Service Areas"
            className="tw-w-full"
            label="Service Area"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={filteredForemanAreas}
            value={selectedForemanAreas}
            onChange={(value) => onFilterChange('foremanArea', value.map((v) => String(v)))}
            getValue={(area) => area.name}
            formatOption={(area) => area.name}
            formatDisplay={(area) => area.name}
            placeholder="All Foreman Areas"
            className="tw-w-full"
            label="Foreman Area"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={filterOptions.contractYears}
            value={selectedContractYears}
            onChange={(value) => onFilterChange('contractYear', value.map((v) => String(v)))}
            getValue={(year) => year}
            formatOption={(year) => year}
            formatDisplay={(year) => year}
            placeholder="All Contract Years"
            className="tw-w-full"
            label="Contract Year"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={filterOptions.activities}
            value={selectedActivities}
            onChange={(value) => onFilterChange('activityId', value.map((v) => String(v)))}
            getValue={(a) => a.id.toString()}
            formatOption={(a) => `${a.activityNumber} - ${a.activityName}`}
            formatDisplay={(a) => a.activityNumber}
            placeholder="All Activities"
            className="tw-w-full"
            label="Activity"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={filteredProjects}
            value={selectedProjects}
            onChange={(value) => onFilterChange('projectId', value.map((v) => String(v)))}
            getValue={(p) => p.id.toString()}
            formatOption={(p) => `${p.projectNumber} - ${p.name}`}
            formatDisplay={(p) => p.projectNumber}
            placeholder="All Projects"
            className="tw-w-full"
            label="Project"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={filterOptions.taskTypes}
            value={selectedTaskTypes}
            onChange={(value) => onFilterChange('taskType', value.map((v) => String(v)))}
            getValue={(t) => t}
            formatOption={(t) => t}
            formatDisplay={(t) => t}
            placeholder="All Types"
            className="tw-w-full"
            label="Task Type"
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={orderedStatuses}
            value={selectedStatuses}
            onChange={(value) => onFilterChange('status', value.map((v) => String(v)))}
            getValue={(status) => status}
            formatOption={(status) => formatStatusForDisplay(status)}
            // Nothing selected still leaves out Cancelled on the server - say so (V2-076).
            placeholder="All Except Cancelled"
            className="tw-w-full"
            label="Status"
            // orderedStatuses is already in lifecycle order; without this the dropdown would
            // re-sort it alphabetically by display label and undo sortTaskStatuses.
            preserveOptionsOrder
          />
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default AllTasksFiltersComponent;
